// src/pricing/seedBuilder.ts
/**
 * Builds the initial catalog seed from productDb static prices (spec §9.1):
 * every VCH- SKU decomposes to base-remainder (+ labor for nodes), with the
 * base back-derived so the forward rollup reproduces the stored price to the
 * cent. Quantities/qty maps from the exec arrive later as richer compositions;
 * this seed is the migration bridge. Values are stringified integers (µ$/bp)
 * matching the GraphQL carrier format.
 */
import { compareQuarters } from '../constants/quarterConfig';
import { solveBaseMicros } from './backDerive';
import { CatalogSnapshot } from './engine';
import { resolveAllAt } from './resolveAt';

export const HW_SKU_PREFIX = 'VCH-';
/**
 * A product the business has withdrawn. Distinct from "priced at zero this
 * quarter": productDb keeps pricing a retired SKU (VCH-5000-D1N still carries
 * $47,096.55), so `storedPriceCents > 0` cannot see retirement and invariant C
 * scopes on exactly that predicate. Only an explicit `false` retires — an
 * absent flag is an ordinary active product.
 *
 * PRICE-AFFECTING as of this slice (SP3a): before this, per-product `isActive`
 * had no consumer anywhere in app code — it was inert, toggled in both
 * directions over time as an ad-hoc "not currently offered" marker with no
 * behavioural effect (e.g. eight SKUs carried it at cccf12c7 2026-02-08,
 * including VCH-5000-J108-2808; back to four by 3e2ccd73 2026-03-31). Now that
 * `isRetired` gates this file's terminator logic, flipping this flag removes a
 * SKU from the published price book. `src/pricing/publishCoverage.real.test.ts`
 * pins the exact `excused` list of currently-retired SKUs and fails loudly if
 * someone flips this flag — intentionally or not — without updating that pin.
 */
const isRetired = (product: any): boolean => product?.isActive === false;
/**
 * Whether a published edition GOVERNS this SKU's price — i.e. whether
 * priceBookFromEdition strips its productDb pricing keys and restores only what
 * the sheet carries. Today: the VCH- hardware SKUs. SP1 widens this to every
 * priced SKU, and widening it HERE widens the publish coverage guard in the
 * same edit, which is the point of it being a function rather than an inlined
 * startsWith. A SKU this returns false for keeps its productDb price and cannot
 * be zeroed by a publish.
 *
 * NOT the only hard-coded HW_SKU_PREFIX check in this file: widening scope
 * also means revisiting quartersFromProductDb and buildSeedFromProductDb
 * below, which filter on the raw prefix independently of this predicate.
 */
export const isGovernedSku = (sku: string): boolean => sku.startsWith(HW_SKU_PREFIX);
const NODE_SKU_RE = /-[DSF]\d*N$/;
export const isNodeSku = (partNumber: string): boolean => NODE_SKU_RE.test(partNumber);

// productDb pricing keys: pricingQ32026 <-> "2026-Q3". THE canonical pair —
// this mapping used to be hand-copied into four modules (Task 3 review
// finding); generateProductDb.ts re-exports `quarterToKey` from here so its
// existing importers keep working against one implementation.
const QUARTER_KEY_RE = /^pricingQ([1-4])(\d{4})$/;
export const quarterFromKey = (k: string): string | null => {
  const m = QUARTER_KEY_RE.exec(k);
  return m ? `${m[2]}-Q${m[1]}` : null;
};
export const quarterToKey = (q: string): string => `pricingQ${q.slice(6)}${q.slice(0, 4)}`;
export const quarterToDiscountKey = (q: string): string =>
  `discountQ${q.slice(6)}${q.slice(0, 4)}`;

/**
 * THE "is this SKU priced in this quarter" predicate, in integer cents.
 *
 * Rounds BEFORE the comparison, so a stored price in (0, 0.005) dollars is
 * unpriced — the same verdict the terminator pass and the remainder solver
 * (remainderRows.ts) must reach, or the solver would emit a remainder in the
 * very quarter a terminator covers, and mergeSeed would throw on the kind
 * conflict. Real productDb data is 2-dp so the two rounding orders agree
 * today; one implementation is what keeps them agreeing (Task 3 review
 * finding). An absent or non-numeric key is 0, i.e. unpriced.
 */
export function storedPriceCents(product: Record<string, unknown>, quarter: string): number {
  const dollars = product[quarterToKey(quarter)];
  return typeof dollars === 'number' ? Math.round(dollars * 100) : 0;
}

/**
 * Every quarter productDb carries an HW pricing key for, across any HW SKU —
 * the PRICED-quarter list (Task 5). This is a DIFFERENT, wider concept than
 * quarterMapping (src/constants/quarterConfig.jsx), the SELLABLE-quarter list
 * the UI offers: productDb's priced range runs 2025-Q3..2028-Q4 (14 quarters)
 * while quarterMapping only covers 2026-Q1..2028-Q4 (12) — see the Task 5
 * addendum's table. Generation and the parity suites need the priced list, not
 * the sellable one. Filtered to HW_SKU_PREFIX so a same-shaped pricingQ* key
 * on some non-HW product can't silently widen this past what "priced HW
 * quarter" actually means.
 */
export function quartersFromProductDb(
  db: { products: Record<string, Record<string, unknown>> },
): string[] {
  return Array.from(new Set(
    Object.entries(db.products)
      .filter(([sku]) => sku.startsWith(HW_SKU_PREFIX))
      .flatMap(([, product]) => Object.keys(product))
      .map(quarterFromKey)
      .filter((q): q is string => q !== null),
  )).sort();
}

// Row kinds whose components are exempt from the integration uplift and pass
// through 1:1 into the price (spec Task16/engine.ts upliftExemptSkus).
const UPLIFT_EXEMPT_KINDS = new Set(['labor', 'chassis']);

export interface CatalogSeedRow {
  vendor: string;
  effectiveFrom: string;    // the quarter this cost takes effect
  componentSku: string;
  // 'unpriced' (Task 3b) asserts ONLY "no cost for this componentSku from
  // this quarter forward" — it makes NO claim about why. Absent-vs-explicit-
  // zero in productDb is NOT a reliable end-of-life signal: DELL has 33 SKUs
  // whose keys simply stop at a pricing-horizon quarter, clustered on two
  // cutoff dates by whole product family, not 33 discontinuations. A real
  // end-of-life flag can be added later when a human actually authors one
  // (YAGNI for now). `value` is an inert placeholder ('0') for this kind —
  // never read as a cost.
  kind: 'component' | 'labor' | 'chassis' | 'integrationPct' | 'baseRemainder' | 'discountPct'
      | 'unpriced';
  value: string;
  recordedAt: string;
  version: number;
}
export interface CatalogSeedComposition {
  vendor: string; productSku: string;
  components: Array<{ componentSku: string; qty: number }>;
  version: number;
}
export interface CatalogSeed { rows: CatalogSeedRow[]; compositions: CatalogSeedComposition[]; }

/**
 * CONF-211: the integration uplift has a timeline. One step says "`bp` applies
 * from quarter `from` (inclusive) until the next step". A step may carry its
 * own `recordedAt`, which stamps the CMP-INT-PCT row it emits: transaction
 * time must be truthful, because `resolveAt` filters on it and an edition
 * reconstructs its basis at its own asOf. A step without one takes the seed's
 * provenance stamp (`SeedBuildOpts.recordedAt`). A plain number is the old
 * flat-rate shape and still means "every quarter".
 */
export interface IntegrationStep { from: string; bp: number; recordedAt?: string }
export type IntegrationSchedule = number | readonly IntegrationStep[];

export interface SeedBuildOpts {
  vendor: string;
  integrationBp: IntegrationSchedule;
  laborMicros: number;
  recordedAt: string;
}

/** The step in force at `quarter`: the latest step whose `from` is at or before
 *  it, in quarter order regardless of authoring order. Throws — never defaults —
 *  for a quarter before the first step, because "no rate yet" priced at 0 % is
 *  exactly the silent defect the schedule exists to prevent. */
export function resolveIntegration(
  schedule: IntegrationSchedule, quarter: string,
): { bp: number; recordedAt?: string } {
  if (typeof schedule === 'number') return { bp: schedule };
  if (schedule.length === 0) throw new Error('integration schedule is empty');
  const steps = [...schedule].sort((a, b) => compareQuarters(a.from, b.from));
  let match: IntegrationStep | undefined;
  for (const step of steps) {
    if (compareQuarters(step.from, quarter) > 0) break;
    match = step;
  }
  if (!match) {
    throw new Error(
      `integration schedule starts after ${quarter} (first step ${steps[0]!.from})`);
  }
  return match.recordedAt === undefined
    ? { bp: match.bp }
    : { bp: match.bp, recordedAt: match.recordedAt };
}

export const integrationBpAt = (schedule: IntegrationSchedule, quarter: string): number =>
  resolveIntegration(schedule, quarter).bp;

/**
 * Collapse consecutive equal assertions per (vendor, componentSku), ordered by
 * effectiveFrom: a row survives only if it says something DIFFERENT from the
 * previous surviving row on its own timeline. resolveAt carries the survivor
 * forward, so the collapsed rows answer exactly the same questions.
 *
 * SEED-TIME ONLY. A generated seed has no re-assertions, so a repeat is pure
 * duplication. On the publish path a repeated value is an explicit "still
 * flat, I checked" confirmation and MUST be preserved (spec §3, decision #3) —
 * which is why buildSeedFromProductDb is the only caller. Exported so the
 * collapse rule can be pinned directly, including the two shapes current data
 * cannot reach (see seedBuilder.test.ts) — not as a publish-path utility.
 *
 * "Says something different" compares (kind, value), NOT value alone. An
 * 'unpriced' terminator carries the inert placeholder value '0', and 20 real
 * componentSkus already solve to a baseRemainder of exactly '0' (the post-
 * CONF-171 JBOD/NVMe remainders), so a value-only comparison would drop a
 * terminator that follows a zero remainder — resurrecting the cost past the
 * SKU's horizon — or drop the remainder that resumes after a terminator.
 * Comparing against the last SURVIVING row rather than the last value seen is
 * what keeps a SKU that resumes at the price it was terminated on.
 */
export function sparsify(rows: CatalogSeedRow[]): CatalogSeedRow[] {
  const groups = new Map<string, CatalogSeedRow[]>();
  for (const r of rows) {
    const k = `${r.vendor}|${r.componentSku}`;
    const g = groups.get(k);
    if (g) g.push(r); else groups.set(k, [r]);
  }
  const out: CatalogSeedRow[] = [];
  for (const g of groups.values()) {
    g.sort((a, b) => compareQuarters(a.effectiveFrom, b.effectiveFrom));
    let last: CatalogSeedRow | undefined;
    for (const r of g) {
      if (last && last.kind === r.kind && last.value === r.value) continue;
      out.push(r);
      last = r;
    }
  }
  return out;
}

export function buildSeedFromProductDb(
  productDb: any,
  opts: SeedBuildOpts,
): CatalogSeed {
  const rows: CatalogSeedRow[] = [];
  const compositions: CatalogSeedComposition[] = [];
  const skus = Object.keys(productDb.products).filter(k => k.startsWith(HW_SKU_PREFIX));
  const quarters = new Set<string>();

  // One global HW discount (ratified: all HW SKUs share it — verified 30% across
  // AIC and DELL). If a SKU ever deviates, fail loudly: the fix is a per-SKU
  // CMP-DISC-<sku> override row, not silently averaging.
  const discounts = new Set<number>(skus.map(s => productDb.products[s].discount ?? 0));
  if (discounts.size !== 1) {
    throw new Error(`HW discounts not uniform (${[...discounts].join(', ')}) — ` +
      'emit per-SKU CMP-DISC-<sku> override rows for the outliers');
  }
  // (noUncheckedIndexedAccess makes Set-destructuring type as T | undefined;
  // a for-of grab of the sole element sidesteps that without changing behavior.)
  let hwDiscountBp = 0;
  for (const d of discounts) { hwDiscountBp = Math.round(d * 10000); break; }

  for (const sku of skus) {
    const product = productDb.products[sku];
    const node = isNodeSku(sku);
    compositions.push({
      vendor: opts.vendor, productSku: sku, version: 1,
      components: node
        ? [{ componentSku: `CMP-BASE-${sku}`, qty: 1 },
           { componentSku: 'CMP-LABOR-NODE', qty: 1 }]
        : [{ componentSku: `CMP-BASE-${sku}`, qty: 1 }],
    });

    for (const key of Object.keys(product)) {
      const quarter = quarterFromKey(key);
      if (!quarter) continue;
      quarters.add(quarter);
      const priceCents = storedPriceCents(product, quarter);
      if (priceCents <= 0) continue;   // zero/absent price: SKU not sold that quarter
      if (isRetired(product)) continue; // retired: terminator pass emits `unpriced` instead
      const base = solveBaseMicros(priceCents, integrationBpAt(opts.integrationBp, quarter),
                                   node ? opts.laborMicros : 0);
      rows.push({ vendor: opts.vendor, effectiveFrom: quarter,
                  componentSku: `CMP-BASE-${sku}`, kind: 'baseRemainder',
                  value: String(base), recordedAt: opts.recordedAt, version: 1 });
    }
  }

  for (const quarter of quarters) {
    const integration = resolveIntegration(opts.integrationBp, quarter);
    rows.push({ vendor: opts.vendor, effectiveFrom: quarter,
                componentSku: 'CMP-INT-PCT', kind: 'integrationPct',
                value: String(integration.bp),
                recordedAt: integration.recordedAt ?? opts.recordedAt, version: 1 });
    rows.push({ vendor: opts.vendor, effectiveFrom: quarter,
                componentSku: 'CMP-LABOR-NODE', kind: 'labor',
                value: String(opts.laborMicros), recordedAt: opts.recordedAt, version: 1 });
    rows.push({ vendor: opts.vendor, effectiveFrom: quarter,
                componentSku: 'CMP-DISC-HW', kind: 'discountPct',
                value: String(hwDiscountBp), recordedAt: opts.recordedAt, version: 1 });
  }

  // Task 3b: terminator pass. Resolution (unlike the old exact-match) carries
  // a SKU's last positive price forward FOREVER once nothing exact-matches a
  // later quarter — correct for an ordinary unchanged quarter, wrong for a
  // SKU that simply stops appearing in productDb (see the `kind` doc comment
  // above: this is not necessarily end-of-life, just "no data past here").
  // Recomputed independently from `productDb` (not from the rows already
  // pushed above) over the FULL cross-SKU quarter set, so a quarter this SKU
  // has no key for at all (not just an explicit 0) is caught too.
  const sortedQuarters = [...quarters].sort();
  for (const sku of skus) {
    const product = productDb.products[sku];
    const retired = isRetired(product);
    let everPriced = false;
    for (const quarter of sortedQuarters) {
      const priced = storedPriceCents(product, quarter) > 0;
      if (priced) everPriced = true;
      if (!everPriced) continue;              // pre-existence gap: no row at all
      if (priced && !retired) continue;       // ordinary priced quarter: has its baseRemainder row
      rows.push({ vendor: opts.vendor, effectiveFrom: quarter,
                  componentSku: `CMP-BASE-${sku}`, kind: 'unpriced',
                  value: '0', recordedAt: opts.recordedAt, version: 1 });
    }
  }

  // Task 6: everything above emits one row per SKU-quarter, the expand-phase
  // shape. resolveAt carries a value forward, so only the CHANGES need to be
  // stored — see sparsify's doc for why this is a seed-time-only step.
  return { rows: sparsify(rows), compositions };
}

/** Assemble the engine-facing snapshot for one quarter from seed rows. */
export function snapshotFromSeed(
  seed: CatalogSeed, quarter: string, asOf?: string,
): CatalogSnapshot {
  const componentCostMicros: Record<string, number> = {};
  const discountBpOverrides: Record<string, number> = {};
  const upliftExemptSkus = new Set<string>();
  let integrationBp: number | undefined;
  let hwDiscountBp: number | undefined;

  for (const [, { row }] of resolveAllAt(seed.rows, quarter, asOf)) {
    const value = Number(row.value);
    if (row.kind === 'integrationPct') integrationBp = value;
    else if (row.kind === 'discountPct') {
      if (row.componentSku === 'CMP-DISC-HW') hwDiscountBp = value;
      else discountBpOverrides[row.componentSku.replace(/^CMP-DISC-/, '')] = value;
    } else if (row.kind === 'unpriced') {
      // The WINNING row for this componentSku at this quarter says "no cost
      // from here forward" — simply not recording a cost is what excludes
      // the composition below (stays "not fully costed"), same mechanism
      // exact-matching used to get for free from an absent row. `continue`
      // (rather than an empty block) pins this: it's only an accident of
      // being the last branch that a later statement appended here wouldn't
      // silently start applying to `unpriced` rows too.
      continue;
    } else {
      componentCostMicros[row.componentSku] = value;   // baseRemainder | labor | chassis | component
      if (UPLIFT_EXEMPT_KINDS.has(row.kind)) upliftExemptSkus.add(row.componentSku);
    }
  }
  // Resolution (unlike the old exact-match) can legitimately resolve NOTHING
  // for a quarter before anything was ever authored (e.g. a synthetic seed
  // queried before its first published quarter) — that's "never priced," not
  // a data bug, and every composition is already excluded below (no cost
  // rows to be fully-costed against). Only fail loudly when something WAS
  // priced this quarter but a global row is unexpectedly missing, which
  // stays a real authoring bug worth throwing on.
  if (Object.keys(componentCostMicros).length > 0) {
    if (integrationBp === undefined) throw new Error(`no integrationPct row for ${quarter}`);
    if (hwDiscountBp === undefined) throw new Error(`no CMP-DISC-HW row for ${quarter}`);
  }
  integrationBp = integrationBp ?? 0;
  hwDiscountBp = hwDiscountBp ?? 0;

  // seed.compositions may carry MULTIPLE versions per productSku (mergeSeed
  // keeps superseded composition versions rather than dropping them, since
  // compositions aren't quarter-scoped but their components' cost rows often
  // are). Select, per SKU, the HIGHEST version whose components are ALL
  // costed THIS quarter — falling back to a lower version (e.g. the
  // baseline CMP-BASE-<sku>-only composition) when a newer decomposition's
  // component costs aren't backed for this quarter. This is what keeps
  // "other quarters baseline" (spec §6) while letting a fully-costed quarter
  // (e.g. 2026-Q3 for the real decomposition) pick up the richer version.
  const compsBySku = new Map<string, CatalogSeedComposition[]>();
  for (const c of seed.compositions) {
    const list = compsBySku.get(c.productSku);
    if (list) list.push(c); else compsBySku.set(c.productSku, [c]);
  }
  const compositions: CatalogSnapshot['compositions'] = {};
  for (const [productSku, versions] of compsBySku) {
    const fullyCosted = versions
      .filter(c => c.components.every(({ componentSku }) => componentCostMicros[componentSku] !== undefined))
      .sort((a, b) => b.version - a.version);
    if (fullyCosted.length > 0) compositions[productSku] = fullyCosted[0]!.components;
  }
  const vendor = seed.compositions[0]?.vendor ?? 'AIC';
  return { vendor, quarter, componentCostMicros, integrationBp,
           hwDiscountBp, discountBpOverrides, compositions, upliftExemptSkus,
           resolvedAsOf: asOf };
}
